// The printed route sheet for a job (or a zone on its own): a preview of the
// two A4 pages, then Save PDF (the desktop app) or Print (a browser).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, ModalShell } from '../lib/ui';
import { docHtml } from '../lib/tradeDocs';
import { savePdf, canAttachPdf } from '../lib/pdf';
import { buildRouteSheet, routeSheetFileName } from '../lib/routeSheet';
import { errorText } from './TradeCommon';

const PAGE_W = (210 * 96) / 25.4;

function SheetPreview({ doc }) {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(PAGE_W * 2.9);

  useLayoutEffect(() => {
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const measure = () => {
    const d = frameRef.current?.contentDocument;
    if (d?.documentElement) setHeight(d.documentElement.scrollHeight);
  };
  const onLoad = () => {
    measure();
    frameRef.current?.contentDocument?.fonts?.ready.then(measure);
  };

  const scale = width ? width / PAGE_W : 0.5;
  return (
    <div ref={wrapRef} className="lf-sheet-preview" style={{ height: height * scale }}>
      <iframe ref={frameRef} title={doc.title} srcDoc={docHtml(doc)} sandbox="allow-same-origin" scrolling="no" onLoad={onLoad}
        style={{ width: PAGE_W, height, transform: `scale(${scale})` }} />
    </div>
  );
}

export default function LeafletsSheet({ zone, job, person, settings, onClose }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let live = true;
    buildRouteSheet({ zone, job, person, settings })
      .then(d => live && setDoc(d))
      .catch(e => live && setError(errorText(e)));
    return () => { live = false; };
  }, [zone, job, person, settings]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await savePdf(doc, { fileName: routeSheetFileName({ zone, person }), folder: 'TDH/Leaflet rounds' });
      if (res.path) setSaved(res.path);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={720} ariaLabel="Route sheet">
      <div className="lf-row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Route sheet{person ? ` for ${person.name}` : ''}</h2>
        <div className="lf-row" style={{ marginLeft: 'auto' }}>
          <Button variant="muted" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={save} disabled={!doc || saving}>{saving ? 'Making the PDF…' : canAttachPdf ? 'Save PDF' : 'Print'}</Button>
        </div>
      </div>
      <p className="lf-muted" style={{ marginBottom: 10 }}>
        Two pages: print them double-sided. The map's on the front, the street checklist and how to send the screenshot on the back.
        {!zone.streets?.length && ' This zone has no street list yet: Find streets in the zone editor adds one.'}
      </p>
      {saved && <div className="ui-banner ui-banner--ok" style={{ marginBottom: 10 }}>Saved to {saved}, and opened to print.</div>}
      {error && <div className="ui-banner ui-banner--bad" style={{ marginBottom: 10 }}>{error}</div>}
      {doc ? <SheetPreview doc={doc} /> : !error && <div className="loader">Drawing the map…</div>}
    </ModalShell>
  );
}
